import React, { useMemo } from 'react';
import { useStore } from '../../stores/useStore';

// ── 고정 공휴일 (매년 동일) ────────────────────────────────────────
const getFixedHolidays = (y: number): string[] => [
    `${y}-01-01`, // 신정
    `${y}-03-01`, // 삼일절
    `${y}-05-05`, // 어린이날
    `${y}-06-06`, // 현충일
    `${y}-08-15`, // 광복절
    `${y}-10-03`, // 개천절
    `${y}-10-09`, // 한글날
    `${y}-12-25`, // 크리스마스
];

// ── 음력 공휴일 + 대체공휴일 + 선거·임시공휴일 (연도별 하드코딩) ──
const SPECIAL_HOLIDAYS = new Set([
    // 2022
    '2022-01-31', '2022-02-01', '2022-02-02',          // 설날
    '2022-03-09',                                         // 대통령선거
    '2022-05-08',                                         // 부처님오신날
    '2022-05-09',                                         // 부처님오신날 대체 (일→월)
    '2022-06-01',                                         // 지방선거
    '2022-09-09', '2022-09-10', '2022-09-11',           // 추석
    '2022-09-12',                                         // 추석 대체 (일→월)
    '2022-10-10',                                         // 한글날 대체 (일→월)
    '2022-12-26',                                         // 크리스마스 대체 (일→월)

    // 2023
    '2023-01-21', '2023-01-22', '2023-01-23',           // 설날
    '2023-01-24',                                         // 설날 대체 (설날 일→화)
    '2023-05-27',                                         // 부처님오신날
    '2023-05-29',                                         // 부처님오신날 대체 (토→월)
    '2023-09-28', '2023-09-29', '2023-09-30',           // 추석
    '2023-10-02',                                         // 임시공휴일 (추석 연휴 징검다리)

    // 2024
    '2024-02-09', '2024-02-10', '2024-02-11', '2024-02-12', // 설날 + 대체 (일→월)
    '2024-04-10',                                         // 국회의원선거
    '2024-05-06',                                         // 어린이날 대체 (일→월)
    '2024-05-15',                                         // 부처님오신날
    '2024-09-16', '2024-09-17', '2024-09-18',           // 추석

    // 2025
    '2025-01-28', '2025-01-29', '2025-01-30',           // 설날
    '2025-03-03',                                         // 삼일절 대체 (토→월)
    '2025-05-05',                                         // 어린이날 + 부처님오신날
    '2025-06-03',                                         // 대통령선거
    '2025-10-05', '2025-10-06', '2025-10-07',           // 추석
    '2025-10-08',                                         // 추석 대체 (전날 일→수)

    // 2026
    '2026-02-16', '2026-02-17', '2026-02-18',           // 설날
    '2026-05-24',                                         // 부처님오신날
    '2026-05-25',                                         // 부처님오신날 대체 (일→월)
    '2026-06-03',                                         // 지방선거
    '2026-08-17',                                         // 광복절 대체 (토→월)
    '2026-09-24', '2026-09-25', '2026-09-26',           // 추석
    '2026-09-28',                                         // 추석 대체 (다음날 토→월)
    '2026-10-05',                                         // 개천절 대체 (토→월)

    // 2027
    '2027-02-06', '2027-02-07', '2027-02-08',           // 설날
    '2027-02-09',                                         // 설날 대체 (설날 일→화)
    '2027-05-13',                                         // 부처님오신날
    '2027-10-14', '2027-10-15', '2027-10-16',           // 추석
    '2027-10-18',                                         // 추석 대체 (다음날 토→월)
]);

const isHoliday = (dateStr: string): boolean => {
    if (SPECIAL_HOLIDAYS.has(dateStr)) return true;
    const [y] = dateStr.split('-').map(Number);
    return getFixedHolidays(y).includes(dateStr);
};

// col: 0=일(red), 6=토(blue), 1~5=평일(black)
const getDayColor = (col: number, dateStr: string, isCurrent: boolean): string => {
    const holiday = isHoliday(dateStr);
    if (!isCurrent) {
        if (col === 0 || holiday) return '#f5b8b8';
        if (col === 6) return '#b3c8f0';
        return '#ccc';
    }
    if (col === 0 || holiday) return '#d32f2f';
    if (col === 6) return '#1565c0';
    return '#2c2c2c';
};

export const CalendarView: React.FC = () => {
    const { selectedDate, setSelectedDate, schedules } = useStore();
    const todayStr = new Date().toLocaleDateString('en-CA');

    const [year, month] = useMemo(() => {
        const [y, m] = selectedDate.split('-').map(Number);
        return [y, m];
    }, [selectedDate]);

    const calendarDays = useMemo(() => {
        const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
        const daysInMonth = new Date(year, month, 0).getDate();
        const daysInPrevMonth = new Date(year, month - 1, 0).getDate();

        const days: { day: number; type: 'prev' | 'current' | 'next'; dateStr: string }[] = [];

        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
            const d = daysInPrevMonth - i;
            const prevMonth = month === 1 ? 12 : month - 1;
            const prevYear = month === 1 ? year - 1 : year;
            days.push({ day: d, type: 'prev', dateStr: `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            days.push({ day: d, type: 'current', dateStr: `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
        }
        const remaining = 42 - days.length;
        for (let d = 1; d <= remaining; d++) {
            const nextMonth = month === 12 ? 1 : month + 1;
            const nextYear = month === 12 ? year + 1 : year;
            days.push({ day: d, type: 'next', dateStr: `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
        }
        return days;
    }, [year, month]);

    const getScheduleCount = (dateStr: string) =>
        schedules.filter((s) => s.date === dateStr).length;

    const isToday = (dateStr: string) => dateStr === todayStr;
    const isSelected = (dateStr: string) => dateStr === selectedDate;

    const handlePrevMonth = () => {
        const newMonth = month === 1 ? 12 : month - 1;
        const newYear = month === 1 ? year - 1 : year;
        setSelectedDate(`${newYear}-${String(newMonth).padStart(2, '0')}-01`);
    };

    const handleNextMonth = () => {
        const newMonth = month === 12 ? 1 : month + 1;
        const newYear = month === 12 ? year + 1 : year;
        setSelectedDate(`${newYear}-${String(newMonth).padStart(2, '0')}-01`);
    };

    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
    const headerColors = ['#d32f2f', '#2c2c2c', '#2c2c2c', '#2c2c2c', '#2c2c2c', '#2c2c2c', '#1565c0'];

    return (
        <div style={{ backgroundColor: '#f4f1ea', padding: '16px', borderBottom: '2px solid #c4b8a5' }}>
            {/* 헤더 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <button onClick={handlePrevMonth} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#1e3a5f' }}>
                    ◀
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e3a5f' }}>
                        {year}년 {month}월
                    </h2>
                    <button
                        onClick={() => setSelectedDate(todayStr)}
                        style={{
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#c75c2e',
                            backgroundColor: 'white',
                            border: '1px solid #c75c2e',
                            borderRadius: '10px',
                            cursor: 'pointer',
                        }}
                    >
                        오늘
                    </button>
                </div>
                <button onClick={handleNextMonth} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#1e3a5f' }}>
                    ▶
                </button>
            </div>

            {/* 요일 헤더 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '8px' }}>
                {weekDays.map((d, i) => (
                    <div key={d} style={{ fontSize: '12px', fontWeight: 700, padding: '4px', color: headerColors[i] }}>
                        {d}
                    </div>
                ))}
            </div>

            {/* 날짜 그리드 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                {calendarDays.map(({ day, type, dateStr }, i) => {
                    const count = getScheduleCount(dateStr);
                    const today = isToday(dateStr);
                    const selected = isSelected(dateStr);
                    const col = i % 7;
                    const isCurrent = type === 'current';
                    const dayColor = getDayColor(col, dateStr, isCurrent);

                    return (
                        <button
                            key={i}
                            onClick={() => setSelectedDate(dateStr)}
                            style={{
                                aspectRatio: '1',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px',
                                backgroundColor: selected ? '#1e3a5f' : today ? '#f0ebe3' : 'transparent',
                                color: selected ? 'white' : dayColor,
                                borderRadius: '10px',
                                border: today && !selected ? '2px solid #c75c2e' : selected ? 'none' : '1px solid transparent',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: today || selected ? 700 : 400,
                                transition: 'all 0.15s',
                            }}
                        >
                            <span>{day}</span>
                            {count > 0 && !selected && (
                                <span style={{
                                    fontSize: '9px',
                                    marginTop: '2px',
                                    padding: '1px 4px',
                                    borderRadius: '10px',
                                    backgroundColor: today ? '#1e3a5f' : '#c75c2e',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontFamily: "'Roboto Mono', monospace",
                                }}>
                                    {count}
                                </span>
                            )}
                            {selected && count > 0 && (
                                <span style={{
                                    fontSize: '9px',
                                    marginTop: '2px',
                                    padding: '1px 4px',
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                    color: 'white',
                                    fontFamily: "'Roboto Mono', monospace",
                                }}>
                                    {count}건
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
